import type { InputSchema } from "./inputValidation";

const operationId: InputSchema = {
  type: "string",
  minLength: 1,
  maxLength: 100,
  description:
    "Unique request ID. Repeat identical arguments to retrieve status after real in-app approval. Use a new ID for a new/revised action, never to bypass a pending approval. Receipts last ten minutes (32 requests), survive editor navigation, and reset on account change/reload.",
};
const role: InputSchema = {
  type: "string",
  enum: ["viewer", "editor"],
  description:
    "Requested recipient access: viewer can view, editor can modify. Select the user's intended permission; do not silently escalate to editor.",
};
const action: InputSchema = {
  type: "string",
  enum: ["read", "create", "start_session"],
  description:
    "read is side-effect-free; create requests a durable cloud people link; start_session requests/reuses a temporary local live-session invitation without creating a cloud file.",
};
export const applicationSchemas = {
  drawing_list: {
    type: "object",
    additionalProperties: false,
    properties: {
      scope: {
        type: "string",
        default: "mine",
        enum: ["mine", "shared"],
        description:
          "mine (default): My Drawings; shared: Shared with Me. Available from a local canvas without first saving it.",
      },
      cursor: {
        type: "string",
        minLength: 1,
        maxLength: 8192,
        description:
          "Opaque nextCursor from drawing_list. Unlike canvas read paging, repeat the SAME scope, query and limit with this cursor. Do not invent or reuse across scopes.",
      },
      query: {
        type: "string",
        minLength: 1,
        maxLength: 120,
        description:
          "Case-insensitive title substring applied to the current hydrated page, not global search. Omit for all titles; follow nextCursor even on zero matches. Missing titles cannot match.",
      },
      limit: {
        type: "integer",
        default: 20,
        minimum: 1,
        maximum: 20,
        description:
          "Backend entries scanned per page, default 20; query may return fewer or zero drawings.",
      },
    },
    examples: [
      {},
      { scope: "shared", query: "Architecture", limit: 20 },
      { scope: "shared", query: "Architecture", limit: 20, cursor: "LIST_NEXT_CURSOR" },
    ],
  },
  drawing_open: {
    type: "object",
    additionalProperties: false,
    properties: {
      operationId,
      drawingId: {
        type: "string",
        minLength: 36,
        maxLength: 36,
        description:
          "Drawing UUID returned by drawing_list, not an element ID, URL, share code or storage path. Example UUID is illustrative; use the selected live result.",
      },
    },
    required: ["operationId", "drawingId"],
    examples: [{ operationId: "open-1", drawingId: "00000000-0000-4000-8000-000000000001" }],
  },
  drawing_save: {
    type: "object",
    additionalProperties: false,
    properties: {
      operationId,
      name: {
        type: "string",
        minLength: 1,
        maxLength: 120,
        description:
          "Nonblank title REQUIRED to create a cloud file from local solo work (trimmed). OMIT for an existing cloud drawing; this tool does not rename files. Leave a local live session in the app before cloud creation.",
      },
    },
    required: ["operationId"],
    examples: [
      { operationId: "save-local-1", name: "Architecture" },
      { operationId: "save-cloud-1" },
    ],
  },
  drawing_share: {
    type: "object",
    additionalProperties: false,
    properties: { action, role, operationId },
    required: ["action"],
    anyOf: [
      {
        type: "object",
        additionalProperties: false,
        properties: { action: { ...action, enum: ["read"] } },
        required: ["action"],
      },
      {
        type: "object",
        additionalProperties: false,
        properties: { action: { ...action, enum: ["create", "start_session"] }, role, operationId },
        required: ["action", "role", "operationId"],
      },
    ],
    examples: [
      { action: "read" },
      { action: "create", role: "viewer", operationId: "cloud-link-1" },
      { action: "create", role: "editor", operationId: "cloud-link-2" },
      { action: "start_session", role: "viewer", operationId: "local-session-1" },
      { action: "start_session", role: "editor", operationId: "local-session-2" },
    ],
  },
} as const satisfies Record<string, InputSchema>;
